/**
 * Ambassador Linking Modal
 * Allows linking Ambassador Knowledge Packs to Product Packs
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getAvailableAmbassadorPacks, linkAmbassadorToPack } from '../api/productPackV4';

export default function AmbassadorLinkingModal({ 
  isOpen, 
  onClose, 
  productPackId,
  onAmbassadorLinked 
}) {
  const [ambassadorPacks, setAmbassadorPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [endorsementContext, setEndorsementContext] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAmbassadorPacks();
    }
  }, [isOpen]);

  const fetchAmbassadorPacks = async () => {
    setLoading(true);
    try {
      const data = await getAvailableAmbassadorPacks();
      setAmbassadorPacks(data.packs || []);
    } catch (error) {
      toast.error(`Failed to load ambassadors: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    if (!selectedPackId) {
      toast.error('Please select an ambassador');
      return;
    }

    setLinking(true);
    try {
      await linkAmbassadorToPack(productPackId, selectedPackId, endorsementContext);
      toast.success('✅ Ambassador linked successfully!');
      onAmbassadorLinked();
      onClose();
    } catch (error) {
      toast.error(`Failed to link: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-blue-600">
          <h2 className="text-xl font-bold text-white">🎭 Link Ambassador Pack</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading ambassadors...</p>
            </div>
          ) : ambassadorPacks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No Ambassador Packs available.</p>
              <p className="text-sm text-gray-500 mt-2">Create Ambassador Packs first in Knowledge Pack Manager.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Ambassador Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Ambassador *
                </label>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {ambassadorPacks.map((pack) => {
                    const content = pack.content || {};
                    const toneProfile = content.tone_profile || {};
                    const emotionalSig = content.emotional_signature || {};
                    
                    return (
                      <div
                        key={pack.pack_id}
                        onClick={() => setSelectedPackId(pack.pack_id)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedPackId === pack.pack_id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-gray-800">{content.full_name || 'Unknown'}</h3>
                              {content.alias && (
                                <span className="text-sm text-gray-500">({content.alias})</span>
                              )}
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-2">
                              <strong>Persona:</strong> {content.persona_brand || 'N/A'}
                            </p>
                            
                            <div className="flex flex-wrap gap-2 mb-2">
                              {toneProfile.primary && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  🎤 {toneProfile.primary}
                                </span>
                              )}
                              {emotionalSig.primary_emotion && (
                                <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded-full">
                                  💝 {emotionalSig.primary_emotion}
                                </span>
                              )}
                              {content.market_influence_category && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                  📊 {content.market_influence_category}
                                </span>
                              )}
                            </div>
                            
                            {content.story_themes && content.story_themes.length > 0 && (
                              <p className="text-xs text-gray-500">
                                <strong>Story Themes:</strong> {content.story_themes.slice(0, 3).join(', ')}
                              </p>
                            )}
                          </div>
                          
                          {selectedPackId === pack.pack_id && (
                            <div className="ml-3">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white">
                                ✓
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Endorsement Context */}
              {selectedPackId && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Endorsement Context (Optional)
                  </label>
                  <textarea
                    value={endorsementContext}
                    onChange={(e) => setEndorsementContext(e.target.value)}
                    placeholder="Why does this ambassador endorse this product? E.g., 'Uses product daily for better health'"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-vertical"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            disabled={linking}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={!selectedPackId || linking || loading}
            className="px-6 py-2 text-sm font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {linking ? '🔗 Linking...' : '🔗 Link Ambassador'}
          </button>
        </div>
      </div>
    </div>
  );
}
